import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPwd) { setError(t("auth.passwordMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password-token", { token, new_password: newPassword });
      toast.success(t("auth.resetDone"));
      navigate("/connexion");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="container-page py-12 flex justify-center">
      <div className="w-full max-w-md">
        <Link to="/connexion" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-secondary mb-4" data-testid="back-to-login"><ArrowLeft className="h-4 w-4" /> {t("auth.loginBtn")}</Link>
        <div className="text-center mb-6">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-primary/10 items-center justify-center"><KeyRound className="h-7 w-7 text-primary" /></span>
          <h1 className="mt-4 font-heading font-extrabold text-2xl text-secondary">{t("auth.resetTitle")}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{t("auth.resetSubtitle")}</p>
        </div>

        {!token ? (
          <div className="rounded-2xl bg-card border border-border p-6 text-center" data-testid="reset-no-token">
            <p className="text-sm text-destructive">{t("auth.resetNoToken")}</p>
            <Button onClick={() => navigate("/mot-de-passe-oublie")} className="mt-4 rounded-full bg-primary hover:bg-primary/90" data-testid="reset-request-again">{t("auth.forgotTitle")}</Button>
          </div>
        ) : (
          <form onSubmit={doReset} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid="reset-token-form">
            <div>
              <Label>{t("auth.newPassword")}</Label>
              <div className="relative mt-1.5">
                <Input required type={showPwd ? "text" : "password"} minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-12 pr-11" data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="reset-toggle-password" aria-label="toggle">
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{t("auth.confirmPassword")}</Label>
              <Input required type={showPwd ? "text" : "password"} minLength={6} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="h-12 mt-1.5" data-testid="reset-confirm-password" />
              {confirmPwd && confirmPwd !== newPassword && <p className="text-xs text-destructive mt-1" data-testid="reset-mismatch">{t("auth.passwordMismatch")}</p>}
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-secondary hover:bg-secondary/90 h-11" data-testid="reset-submit">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("auth.resetBtn")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
